import React from "react";
import { Link } from "react-router-dom";
import { MessageSquare, MousePointerClick, Lock } from "lucide-react";
import { useRole } from "../../lib/role.jsx";
import { useCopilot } from "../../lib/copilot.jsx";
import { useKnowledge } from "../../lib/knowledge.jsx";
import { Meter, StatusIcon } from "./shared";
import { cn } from "../../lib/utils";

const btn = "inline-flex items-center justify-center gap-1.5 h-9 px-3 text-[12.5px] font-medium rounded-sm transition-[opacity,border-color,color,transform] active:scale-[0.98]";

// "Try it on the platform" — only a live link when the signed-in role can open the screen.
export function TryIt({ t, block, where }) {
  const sfx = where ? `-${where}` : "";
  const { canAccess, isHidden } = useRole();
  if (!t.try_it) return null;
  const { route, label } = t.try_it;
  const path = route.split("?")[0];
  if (canAccess(path)) {
    return <Link to={route} data-testid={`kc-try-it${sfx}`} className={cn(btn, "bg-primary text-primary-fg hover:opacity-90", block && "w-full")}><MousePointerClick size={13} />Try it: {label}</Link>;
  }
  return (
    <span data-testid={`kc-try-it-locked${sfx}`} title={isHidden(path) ? "This screen is paused for the pilot phase" : "Your role can't open this screen"}
          className={cn(btn, "border border-dashed border-border text-muted cursor-not-allowed", block && "w-full")}>
      <Lock size={12} />{isHidden(path) ? "Screen paused for the pilot" : "Screen not available to your role"}
    </span>
  );
}

export function AskCopilot({ t, block, where }) {
  const { askCopilot } = useCopilot();
  return (
    <button data-testid={`kc-ask-copilot${where ? `-${where}` : ""}`} onClick={() => askCopilot(t.copilot.prompt, t.copilot.context)}
            className={cn(btn, "border border-primary/50 text-primary hover:bg-primary/10", block && "w-full")}>
      <MessageSquare size={13} />Ask Copilot about this
    </button>
  );
}

// Slim right-hand panel: on this page, progress and actions.
export default function SidePanel({ t }) {
  const { progress, isDone } = useKnowledge();
  const rec = progress?.topics?.[t.slug];
  const area = progress?.areas.find(a => a.id === t.area);
  const links = [...t.sections.map(s => [s.id, s.heading]), ["why", "Why it matters"],
    ...(t.sensitive.length ? [["steps", "Step by step"]] : []), ["example", "Worked example"], ["quiz", "Quiz"]];
  return (
    <aside className="hidden xl:block" data-testid="kc-side">
      <div className="sticky top-28 space-y-5">
        <div>
          <div className="kpi-label mb-2">On this page</div>
          <ul className="border-l border-border space-y-0.5">
            {links.map(([id, label]) => (
              <li key={id}><a href={`#${id}`} data-testid={`kc-onpage-${id}`} className="block -ml-px border-l border-transparent hover:border-primary pl-3 py-1 text-[12.5px] text-muted hover:text-fg transition-colors"
                              onClick={e => { e.preventDefault(); document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }); }}>{label}</a></li>
            ))}
          </ul>
        </div>
        <div className="tile p-4" data-testid="kc-progress-card">
          <div className="flex items-center gap-2 text-[12.5px] font-medium"><StatusIcon done={isDone(t.slug)} />{isDone(t.slug) ? "Topic complete" : "Not complete yet"}</div>
          <div className="text-[11.5px] text-muted mt-1 font-mono-tabular">{rec ? `Best ${rec.best}/5 · ${rec.attempts} attempt${rec.attempts === 1 ? "" : "s"}` : "Pass the quiz (4 of 5)"}</div>
          {area && <>
            <div className="text-[10.5px] uppercase tracking-widest text-muted mt-3 mb-1.5">{t.area_title} · {area.done}/{area.total}</div>
            <Meter done={area.done} total={area.total} />
          </>}
        </div>
        <div className="space-y-2"><TryIt t={t} block /><AskCopilot t={t} block /></div>
      </div>
    </aside>
  );
}
